import React, {useEffect, useState} from "react";
import Select from "react-select";
import {Col, Row} from "react-bootstrap";
import {TutorSelect} from "../Tutor/TutorSelect";
import {UserService} from "../../Services/User/UserService";
import {useQuery} from "react-query";
import {getChildrenDetail} from "../../Api/Query/ChildrenQuery";
import {getUserDetail} from "../../Api/Query/UserQuery";
import {FormLabelRequiredAsterisk} from "../Form/FormLabelRequiredAsterisk";

interface Props {
    value: any,
    onChange: (newValue: any, socialWorker: string) => void,
    onUnemployedTutorChange?: (tutorName: string) => void,
    socialWorker?: string,
    classes?: string,
    isSocialWorkerInvalid?: boolean,
    isAdminView?: boolean,
    unemployedTutor?: string
}

export const DISCOUNT_TYPE_ORDINARY_ID = 'ordinary'
export const DISCOUNT_TYPE_ORDINARY_LABEL = 'Ordinaria (sin descuento)'

export const DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_ID = 'social_services_review'
export const DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_LABEL = 'Solicita Valoración de Servicios Sociales (-100%)'

export const DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_ID = 'two_or_more_unemployed_in_family'
export const DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_LABEL = 'Descuento por 2 o más desempleados en la familia (-50%)'

export const DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_ID = 'one_unemployed_in_family'
export const DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_LABEL = 'Descuento por 1 desempleado en la familia (-20%)'

export const DISCOUNT_TYPE_UNEMPLOYED_MEMBER_IDS = [
    DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_ID,
    DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_ID
];

export const DISCOUNT_TYPE_LARGE_FAMILY_ID = 'large_family'
export const DISCOUNT_TYPE_LARGE_FAMILY_LABEL = 'Descuento por familia numerosa (-20%)'

export const DISCOUNT_TYPE_LABELS_BY_ID = {
    [DISCOUNT_TYPE_ORDINARY_ID]: DISCOUNT_TYPE_ORDINARY_LABEL,
    [DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_ID]: DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_LABEL,
    [DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_ID]: DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_LABEL,
    [DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_ID]: DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_LABEL,
    [DISCOUNT_TYPE_LARGE_FAMILY_ID]: DISCOUNT_TYPE_LARGE_FAMILY_LABEL,
}

export const DISCOUNT_TYPE_PERCENTS_BY_ID = {
    [DISCOUNT_TYPE_ORDINARY_ID]: 0,
    [DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_ID]: 100,
    [DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_ID]: 50,
    [DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_ID]: 20,
    [DISCOUNT_TYPE_LARGE_FAMILY_ID]: 20,
}

export const DiscountTypeSelect: React.FC<Props> = (props) => {

    const [option, setOption] = useState<any>(null)
    const [socialWorker, setSocialWorker] = useState<string>('')
    const [isSocialWorkerInvalid, setSocialWorkerInvalid] = useState<boolean>(props.isSocialWorkerInvalid ?? true)
    const [unemployedTutors, setUnemployedTutors] = useState<any>([])
    const [selectedUnemployedTutor, setSelectedUnemployedTutor] = useState<any>(null)
    const isAdminView = props.isAdminView ?? false

    const userService = new UserService()
    const userId = userService.getId()

    const userDetailQuery = useQuery({
        enabled: false,
        queryKey: ['userDetail', userId],
        queryFn: () => getUserDetail(userId ?? '-1'),
        onSuccess: (response: any) => {
            const tutorOptions: any = []
            const result: any = response.data._result;
            const tutor: any = result?.tutor
            const firstTutor : string = tutor.name + ' ' + tutor.surnames
            tutorOptions.push({
                label: firstTutor,
                value: firstTutor
            })

            let secondTutor: string|null = null

            if(tutor.extraTutorInfo?.name){
                secondTutor = tutor.extraTutorInfo?.name + ' ' + tutor.extraTutorInfo?.surnames
                tutorOptions.push({
                    label: secondTutor,
                    value: secondTutor
                })
            }

            setUnemployedTutors(tutorOptions)
        }
    })

    const options = [
        {
            label: DISCOUNT_TYPE_ORDINARY_LABEL,
            value: DISCOUNT_TYPE_ORDINARY_ID
        },
        {
            label: DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_LABEL,
            value: DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_ID
        },
        {
            label: DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_LABEL,
            value: DISCOUNT_TYPE_TWO_OR_MORE_UNENPLOYED_IN_FAMILY_ID
        },
        {
            label: DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_LABEL,
            value: DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_ID
        },
        {
            label: DISCOUNT_TYPE_LARGE_FAMILY_LABEL,
            value: DISCOUNT_TYPE_LARGE_FAMILY_ID
        }
    ]

    const onChange = (option: any) => {
        let currentSocialWorker = socialWorker
        setOption(option ?? null)

        if (!option || option.value !== DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_ID) {
            currentSocialWorker = ''
            setSocialWorker(currentSocialWorker)
        }

        props.onChange(option, currentSocialWorker)
    }

    const onChangeSocialWorker = (currentSocialWorker: string) => {
        setSocialWorker(currentSocialWorker)
        props.onChange(option, currentSocialWorker)
    }

    const showUnemployedTutorSelect = () => {
        return option && DISCOUNT_TYPE_ONE_UNENPLOYED_IN_FAMILY_ID === option.value
    }

    useEffect(() => {
        if(props.isSocialWorkerInvalid === undefined){
            return
        }

        setSocialWorkerInvalid(props.isSocialWorkerInvalid)
    }, [props.isSocialWorkerInvalid]);

    useEffect(() => {
        if (!props.value || !options.length) {
            return
        }

        const values = options.filter(option => option.value === props.value?.value)
        setOption(values[0] ?? null)
    }, [props.value])

    useEffect(() => {
        if (!options.length) {
            return
        }

        setSocialWorker(props.socialWorker ?? '')
    }, [props.socialWorker]);

    useEffect(() => {
        if(!userDetailQuery?.data){
            userDetailQuery.refetch()
        }
    }, option)

    return (
        <>
            <Select
                options={options}
                placeholder={""}
                noOptionsMessage={() => "No hay resultados"}
                isClearable={true}
                loadingMessage={() => "Cargando ..."}
                onChange={onChange}
                className={props.classes ?? ''}
                value={option}
            />

            {
                option && option.value === DISCOUNT_TYPE_SOCIAL_SERVICES_REVIEW_ID && <>
                    <p className={"mt-3"}>En este caso debe solicitar cita previa con la trabajadora social.</p>
                    <p><strong>¿Ya es solicitante de Servicios Sociales?</strong></p>
                    <label htmlFor={"social_worker"} className={"mb-1"}>Indique el nombre del Trabajador/a Social</label>
                    <input
                        id={"social_worker"}
                        type={"text"}
                        className={"form-control " + (isSocialWorkerInvalid ? "is-invalid" : "")}
                        value={socialWorker}
                        onChange={(e: any) => onChangeSocialWorker(e.target.value)}
                    />
                    {isSocialWorkerInvalid && <p className={"text-danger pt-2"}>El trabajador social es obligatorio para este tipo de descuento.</p>}
                </>
            }

            {
                showUnemployedTutorSelect() &&
                <>
                    <p className={"mt-3"}><strong><FormLabelRequiredAsterisk/> Indica la persona desempleada:</strong></p>
                    {
                        isAdminView && <>{props.unemployedTutor}</>
                    }
                    {
                        !isAdminView &&
                        <TutorSelect
                            isLoading={userDetailQuery.isRefetching || userDetailQuery.isLoading}
                            defaultOptions={unemployedTutors}
                            onChange={(selectedOption: any) => {
                                setSelectedUnemployedTutor(selectedOption)
                                props.onUnemployedTutorChange && props.onUnemployedTutorChange(selectedOption?.value ?? '')
                            }}
                            value={selectedUnemployedTutor}
                        />
                    }
                </>
            }
        </>
    )
}
